import { useState, useMemo } from 'react'
import { ChevronDown, Loader2 } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useDepartments, useClasses, useSubclasses } from '@/hooks/useHierarchy'
import { cn } from '@/utils/cn'

export interface HierarchyFilterValue {
  departmentId: string | null
  classId: string | null
  subclassId: string | null
}

interface HierarchyFilterProps {
  value: HierarchyFilterValue
  onValueChange: (value: HierarchyFilterValue) => void
  className?: string
}

export function HierarchyFilter({
  value,
  onValueChange,
  className,
}: HierarchyFilterProps) {
  const [open, setOpen] = useState(false)

  const { data: departments, isLoading: depsLoading } = useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(
    value.departmentId
  )
  const { data: subclasses, isLoading: subclassesLoading } = useSubclasses(
    value.classId
  )

  const isInitialLoading = depsLoading && open

  const breadcrumb = useMemo(() => {
    const parts: string[] = []
    if (value.departmentId && departments) {
      const dep = departments.find(d => d.id === value.departmentId)
      if (dep) parts.push(dep.name)
    }
    if (value.classId && classes) {
      const cls = classes.find(c => c.id === value.classId)
      if (cls) parts.push(cls.name)
    }
    if (value.subclassId && subclasses) {
      const sub = subclasses.find(s => s.id === value.subclassId)
      if (sub) parts.push(sub.name)
    }
    return parts.length > 0 ? parts.join(' / ') : null
  }, [value, departments, classes, subclasses])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-10 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer whitespace-nowrap',
            className
          )}
        >
          <span className="truncate max-w-[200px] text-foreground">
            {breadcrumb ?? 'All Hierarchies'}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        {isInitialLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {/* Department */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Department
              </label>
              <Select
                value={value.departmentId ?? 'all'}
                onValueChange={v =>
                  onValueChange({
                    departmentId: v === 'all' ? null : v,
                    classId: null,
                    subclassId: null,
                  })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments?.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Class */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Class
              </label>
              <Select
                value={value.classId ?? 'all'}
                onValueChange={v =>
                  onValueChange({
                    ...value,
                    classId: v === 'all' ? null : v,
                    subclassId: null,
                  })
                }
                disabled={!value.departmentId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={classesLoading ? 'Loading...' : 'All Classes'}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {classes?.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subclass */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Subclass
              </label>
              <Select
                value={value.subclassId ?? 'all'}
                onValueChange={v =>
                  onValueChange({
                    ...value,
                    subclassId: v === 'all' ? null : v,
                  })
                }
                disabled={!value.classId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      subclassesLoading ? 'Loading...' : 'All Subclasses'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subclasses</SelectItem>
                  {subclasses?.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
